import { useState } from 'react';
import type { EntityState } from '../../lib/api.js';
import { useDevices } from '../../context/DevicesContext.js';
import { Toggle } from '../ui/Toggle.js';
import { Slider } from '../ui/Slider.js';
import { friendlyName } from '../../lib/utils.js';
import { useToast } from '../ui/Toast.js';

interface Props {
  entityId: string;
  entity: EntityState;
}

export function LightCard({ entityId, entity }: Props) {
  const { sendCommand, patchEntity } = useDevices();
  const toast = useToast();
  const isOn = entity.state === 'on';
  const brightness =
    typeof entity.attributes['brightness'] === 'number'
      ? (entity.attributes['brightness'] as number)
      : 0;
  const [pending, setPending] = useState(false);

  const handleToggle = async (next: boolean) => {
    const prev = entity;
    patchEntity(entityId, { ...entity, state: next ? 'on' : 'off' });
    setPending(true);
    try {
      await sendCommand('light', next ? 'turn_on' : 'turn_off', entityId);
    } catch {
      patchEntity(entityId, prev);
      toast.show('Command failed', 'error');
    } finally {
      setPending(false);
    }
  };

  const handleBrightness = async (v: number) => {
    const prev = entity;
    patchEntity(entityId, {
      ...entity,
      state: 'on',
      attributes: { ...entity.attributes, brightness: v },
    });
    try {
      await sendCommand('light', 'turn_on', entityId, { brightness: v });
    } catch {
      patchEntity(entityId, prev);
      toast.show('Command failed', 'error');
    }
  };

  return (
    <div
      className="p-4 rounded-xl border flex flex-col gap-3"
      style={{ backgroundColor: 'var(--ac-surface)', borderColor: 'var(--ac-border)' }}
    >
      <div className="flex items-center justify-between">
        <div>
          <div className="font-medium text-[var(--ac-text)]">
            {friendlyName(entityId, entity.attributes)}
          </div>
          <div className="text-xs text-[var(--ac-text-muted)]">{isOn ? 'On' : 'Off'}</div>
        </div>
        <Toggle checked={isOn} onChange={handleToggle} disabled={pending} />
      </div>
      {isOn ? (
        <Slider
          value={brightness}
          min={0}
          max={255}
          onChange={handleBrightness}
          label="Brightness"
        />
      ) : null}
    </div>
  );
}
